import { Button } from '@wheely/ui-kit';
import React from 'react';
import { Control } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { TUpdateOwnDriverInfoForm } from '@/modules/driver/model/driver';
import { useLicensingAuthorityTypes } from '@/shared/hooks/useLicensingAuthorityTypes';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';
import { IDocsListItem } from '@src-common/view/components/DocsList';

import { ProfileDocsList } from '../ProfileDocsList';

import { messages } from './messages';

type IEditOwnDriverViewProps<T extends IDocsListItem> = {
  control: Control<TUpdateOwnDriverInfoForm>;
  isSubmitting?: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  docs: T[];
  onUploadDoc: (event: React.SyntheticEvent, document: T) => void;
};

const EditOwnDriverInfoView = <T extends IDocsListItem>({
  onSubmit,
  control,
  isSubmitting,
  docs,
  onUploadDoc,
}: IEditOwnDriverViewProps<T>) => {
  const intl = useIntl();

  const licensingAuthorityTypes = useLicensingAuthorityTypes();
  const links = useExternalLinks();

  return (
    <form onSubmit={onSubmit}>
      <SelectField
        name="licensingAuthority"
        title={intl.formatMessage(messages.licensingAuthorityTitle)}
        description={intl.formatMessage(messages.licensingAuthorityDesc, {
          a: (text: React.ReactNode) => (
            <a href={links.licensingAuthorities} target="_blank" rel="noreferrer">
              {text}
            </a>
          ),
        })}
        control={control}
        items={licensingAuthorityTypes}
      />
      <TextField
        name="licenseNumber"
        title={intl.formatMessage(messages.driverLicenseTitle)}
        placeholder="123456"
        description={intl.formatMessage(messages.driverLicenseDesc)}
        control={control}
        isRequired={true}
      />
      <TextField
        name="homeAddress"
        title={intl.formatMessage(messages.addressTitle)}
        control={control}
        isRequired={false}
      />

      <ProfileDocsList<T> onUploadDoc={onUploadDoc} docs={docs} />

      <BottomButtons>
        <Button type="submit" disabled={isSubmitting}>
          <FormattedMessage defaultMessage="Save" id="RT8KNi" description="Save button text" />
        </Button>
      </BottomButtons>
    </form>
  );
};

export type { IEditOwnDriverViewProps };
export { EditOwnDriverInfoView };
